import { useState, useEffect, useCallback } from 'react'
import { Link, useLocation } from 'react-router'
import { Home, Bed, CalendarCheck, Image, Compass } from 'lucide-react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'

const NAV_ITEMS = [
  { to: '/', label: 'Trang chủ', sectionId: 'chi-nhanh', icon: Home },
  { to: '/phong-nghi', label: 'Phòng nghỉ', sectionId: 'phong-nghi', icon: Bed },
  { to: '/dat-phong', label: 'Đặt phòng', sectionId: 'dat-phong', icon: CalendarCheck },
  { to: '/thu-vien-anh', label: 'Thư viện', sectionId: 'thu-vien-anh', icon: Image },
  { to: '/huong-dan', label: 'Hướng dẫn', sectionId: null, icon: Compass },
] as const

const SCROLL_OFFSET = 180

export function BottomNavigation() {
  const location = useLocation()
  const [activeTab, setActiveTab] = useState<string>('/')
  const [activeSection, setActiveSection] = useState<string | null>(null)
  const isHomePage = location.pathname === '/'

  // Scroll spy to highlight bottom tabs on scroll
  const computeActiveSection = useCallback(() => {
    if (!isHomePage) return null
    const scrollY = window.scrollY + SCROLL_OFFSET

    const sectionIds = ['chi-nhanh', 'phong-nghi', 'thu-vien-anh', 'dat-phong']
    for (const id of sectionIds) {
      const el = document.getElementById(id)
      if (el && el.offsetTop <= scrollY) return id
    }
    return 'chi-nhanh'
  }, [isHomePage])

  useEffect(() => {
    if (!isHomePage) {
      setActiveSection(null)
      // Highlight matching route
      const matched = NAV_ITEMS.find((item) => item.to !== '/' && location.pathname.startsWith(item.to))
      setActiveTab(matched ? matched.to : '/')
      return
    }

    const update = () => {
      const section = computeActiveSection()
      setActiveSection(section)
      if (section) {
        const matched = NAV_ITEMS.find((item) => item.sectionId === section)
        setActiveTab(matched ? matched.to : '/')
      }
    }

    update() // run once
    let ticking = false
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          update()
          ticking = false
        })
        ticking = true
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isHomePage, location.pathname, computeActiveSection])

  const handleTabClick = (e: React.MouseEvent, item: typeof NAV_ITEMS[number]) => {
    if (isHomePage && item.sectionId) {
      const el = document.getElementById(item.sectionId)
      if (el) {
        e.preventDefault()
        const top = el.offsetTop - 80
        window.scrollTo({ top, behavior: 'smooth' })
      }
    }
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 flex h-16 border-t border-white/10 bg-[#ef6666]/95 text-white backdrop-blur-md shadow-[0_-4px_16px_rgba(0,0,0,0.15)] lg:hidden">
      <div className="mx-auto flex w-full max-w-lg items-center justify-around px-2">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon
          const isHighlighted = isHomePage
            ? item.sectionId === activeSection || (item.to === '/' && !activeSection)
            : location.pathname.startsWith(item.to) && item.to !== '/'

          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={(e) => handleTabClick(e, item)}
              className="relative flex h-full w-16 flex-col items-center justify-center gap-1 text-white/70 transition-colors duration-200 active:scale-95"
            >
              <div
                className={cn(
                  'flex size-9 items-center justify-center rounded-xl transition-all duration-200',
                  isHighlighted ? 'text-white' : 'text-white/60'
                )}
              >
                <Icon className="size-5.5 stroke-[2.2]" />
              </div>
              <span
                className={cn(
                  'text-[10px] font-medium tracking-wide transition-all duration-200',
                  isHighlighted ? 'text-white scale-105 font-semibold' : 'text-white/60'
                )}
              >
                {item.label}
              </span>

              {/* Active selection dot indicator using Framer Motion */}
              {isHighlighted && (
                <motion.div
                  layoutId="bottomNavActiveDot"
                  className="absolute bottom-1.5 h-1 w-1 rounded-full bg-white"
                  transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                />
              )}
            </Link>
          )
        })}
      </div>
    </nav>
  )
}
